// Static catalog of the six doctrine archetypes — display metadata only.
// Mechanical effects live in the sim (see docs/02-systems/doctrine-system.md).

import { Atom, Hammer, Leaf, type LucideIcon, Skull, Sparkles, Wand2 } from 'lucide-react';

export type Archetype = 'industry' | 'science' | 'faith' | 'arcane' | 'nature' | 'chaos';

export type DoctrineToneToken =
  | 'doctrine-industry'
  | 'doctrine-science'
  | 'doctrine-faith'
  | 'doctrine-arcane'
  | 'doctrine-nature'
  | 'doctrine-chaos';

export interface ArchetypeMeta {
  readonly id: Archetype;
  readonly name: string;
  readonly Icon: LucideIcon;
  readonly blurb: string;
  /** Token name in globals.css → resolved via `var(--color-${tone})`. */
  readonly tone: DoctrineToneToken;
  /**
   * Per-tier unlock summaries, indexed by the tier number that the next
   * investment will reach. `tierUnlocks[0]` is what tier-1 unlocks;
   * `tierUnlocks[points]` is what the player will gain from their next
   * doctrine point. Optional for backward compat (older meta objects and
   * tests that build one inline).
   */
  readonly tierUnlocks?: ReadonlyArray<string>;
}

export const TIER_CAP = 5;

// Tier unlocks mirror the progression in docs/02-systems/doctrine-system.md.
// Strings are intentionally short (one line, ≤ 60 chars) so they fit inside
// the existing card width without breaking the visual hierarchy.
export const ARCHETYPES: ReadonlyArray<ArchetypeMeta> = [
  {
    id: 'industry',
    name: 'Industry',
    Icon: Hammer,
    blurb: 'Order through making.',
    tone: 'doctrine-industry',
    tierUnlocks: [
      'Smithing guilds and craftsmen',
      'Forges and Mining Guilds',
      'Mass Industry and Logistics Corps',
      'Factory Cities and Steampunk Pacts',
      'Megastructure projects',
    ],
  },
  {
    id: 'science',
    name: 'Science',
    Icon: Atom,
    blurb: 'Truth through knowledge.',
    tone: 'doctrine-science',
    tierUnlocks: [
      'Scholarship, Wheel and Calendar',
      'Universities and Optics',
      'Printing Press and Astronomy',
      'Computing and Rocketry',
      'Ascended Minds and Singularities',
    ],
  },
  {
    id: 'faith',
    name: 'Faith',
    Icon: Sparkles,
    blurb: 'Strength through belief.',
    tone: 'doctrine-faith',
    tierUnlocks: [
      'Shrines and Tribal Priests',
      'Temples and Holy Orders',
      'Missionaries and Theocracy',
      'Mega-Churches and Miracles',
      'Apotheosis and Saintly Reliquaries',
    ],
  },
  {
    id: 'arcane',
    name: 'Arcane',
    Icon: Wand2,
    blurb: 'Power through mystery.',
    tone: 'doctrine-arcane',
    tierUnlocks: [
      'Hedge Wizards and Ley-Touched',
      'Mage Towers and Familiar Bonds',
      'Planar Conduits and Binding',
      'Spell-Storms and Dragon Pacts',
      'Godlike Archmage and Planar Sovereign',
    ],
  },
  {
    id: 'nature',
    name: 'Nature',
    Icon: Leaf,
    blurb: 'Harmony through life.',
    tone: 'doctrine-nature',
    tierUnlocks: [
      'Druidic Circles and Beast-Tamers',
      'Sentinels and Ley Line Walkers',
      'Treant Lords and World Roots',
      'Leviathan-Bond and World-Shaping',
      'Gaia Avatar and Biosphere Merge',
    ],
  },
  {
    id: 'chaos',
    name: 'Chaos',
    Icon: Skull,
    blurb: 'Freedom through disorder.',
    tone: 'doctrine-chaos',
    tierUnlocks: [
      'Raider Warbands and Blood Sacrifice',
      'Sabotage Cells and Plague-Seeded',
      'Warp Rifts and Demon Bound',
      'Apocalypse Cults and Reality Warps',
      'Chaos Incarnate and False-God Summons',
    ],
  },
];
